/**
 * Extracted handler logic for ListDetailPage.
 * Contains all CRUD handlers, optimistic reorder, and drag-end callbacks.
 *
 * W6 rewrite: every API call now passes `holderId` (taken from the
 * Holder switcher upstream). Optimistic updates target the new
 * `sections(holder, list)` and `items(holder, list)` cache slices
 * instead of a single combined detail cache. Section reordering folds
 * into `updateSection({sortOrder})` since the backend no longer has a
 * bulk `reorderTodoSections` verb in V-1.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';

interface UseListDetailHandlersOptions {
  holderId: string;
  listId: string;
  /** Whether the list requires items to pass an "In Review" step before being
   * completed. Drives {@link handleToggleItem}. */
  reviewMode: boolean;
  sections: TodoSectionResponse[];
  items: TodoItemResponse[];
  createSection: (input: {
    name: string;
    description?: string;
    parentSectionId?: string | null;
    sectionType?: 'timeframe' | 'category';
    sortOrder?: number;
  }) => Promise<TodoSectionResponse>;
  updateSection: (vars: {
    sectionId: string;
    input: {
      name?: string;
      description?: string | null;
      sortOrder?: number;
      sectionType?: 'timeframe' | 'category';
      parentSectionId?: string | null;
      hiddenFromPublic?: boolean;
    };
  }) => Promise<TodoSectionResponse>;
  deleteSection: (sectionId: string) => Promise<void>;
  createItem: (input: {
    title: string;
    description?: string;
    sectionId?: string | null;
  }) => Promise<TodoItemResponse>;
  updateItem: (vars: {
    itemId: string;
    input: {
      title?: string;
      description?: string | null;
      status?: 'pending' | 'in_progress' | 'in_review' | 'completed';
      sectionId?: string | null;
      sortOrder?: number;
    };
  }) => Promise<TodoItemResponse>;
  deleteItem: (itemId: string) => Promise<void>;
  reorderItems: (order: string[]) => Promise<void>;
  captureSnapshot: () => void;
  undo: () => void;
}

interface DialogActions {
  setCreateSectionOpen: (open: boolean) => void;
  setCreateTimeframeOpen: (open: boolean) => void;
  setCreateItemOpen: (open: boolean) => void;
  setEditSectionOpen: (open: boolean) => void;
  setEditingSection: (section: TodoSectionResponse | null) => void;
  setEditItemOpen: (open: boolean) => void;
  setEditingItem: (item: TodoItemResponse | null) => void;
}

export function useListDetailHandlers(
  options: UseListDetailHandlersOptions,
  dialogs: DialogActions,
) {
  const {
    holderId,
    listId,
    reviewMode,
    sections,
    items,
    createSection,
    updateSection,
    deleteSection,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    captureSnapshot,
    undo,
  } = options;

  const queryClient = useQueryClient();
  const sectionsKey = queryKeys.todoLists.sections(holderId, listId);
  const itemsKey = queryKeys.todoLists.items(holderId, listId);
  const myKey = queryKeys.todoLists.my(holderId);
  const detailKey = queryKeys.todoLists.detail(holderId, listId);
  const { toast } = useToast();

  // ---------------------------------------------------------------------------
  // Optimistic reorder
  // ---------------------------------------------------------------------------

  const optimisticSectionReorder = useCallback(
    async (reorderedIds: string[]) => {
      captureSnapshot();
      await queryClient.cancelQueries({ queryKey: sectionsKey });
      // Optimistic: shuffle sortOrder locally; the backend doesn't yet
      // expose a bulk reorder verb so we fire one updateSection per row.
      queryClient.setQueryData<{ sections: TodoSectionResponse[] }>(sectionsKey, (old) => {
        if (!old) return old;
        return {
          sections: old.sections.map((s) => {
            const idx = reorderedIds.indexOf(s.sectionId);
            return idx !== -1 ? { ...s, sortOrder: idx } : s;
          }),
        };
      });
      try {
        await Promise.all(
          reorderedIds.map((sectionId, idx) =>
            updateSection({ sectionId, input: { sortOrder: idx } }),
          ),
        );
      } finally {
        queryClient.invalidateQueries({ queryKey: sectionsKey });
      }
    },
    [captureSnapshot, queryClient, sectionsKey, updateSection],
  );

  const optimisticItemReorder = useCallback(
    async (reorderedIds: string[]) => {
      captureSnapshot();
      await queryClient.cancelQueries({ queryKey: itemsKey });
      queryClient.setQueryData<{ items: TodoItemResponse[] }>(itemsKey, (old) => {
        if (!old) return old;
        return {
          items: old.items.map((item) => {
            const idx = reorderedIds.indexOf(item.itemId);
            return idx !== -1 ? { ...item, sortOrder: idx } : item;
          }),
        };
      });
      try {
        await reorderItems(reorderedIds);
      } finally {
        queryClient.invalidateQueries({ queryKey: itemsKey });
      }
    },
    [captureSnapshot, queryClient, itemsKey, reorderItems],
  );

  // ---------------------------------------------------------------------------
  // Drag-end handlers
  // ---------------------------------------------------------------------------

  const createTopLevelDragEnd = useCallback(
    (topLevelSections: TodoSectionResponse[]) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = topLevelSections.findIndex((s) => s.sectionId === String(active.id));
      const newIndex = topLevelSections.findIndex((s) => s.sectionId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(topLevelSections, oldIndex, newIndex);
        optimisticSectionReorder(reordered.map((s) => s.sectionId));
      }
    },
    [optimisticSectionReorder],
  );

  const createUnsortedItemDragEnd = useCallback(
    (unsortedItems: TodoItemResponse[]) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = unsortedItems.findIndex((i) => i.itemId === String(active.id));
      const newIndex = unsortedItems.findIndex((i) => i.itemId === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(unsortedItems, oldIndex, newIndex);
        optimisticItemReorder(reordered.map((i) => i.itemId));
      }
    },
    [optimisticItemReorder],
  );

  // ---------------------------------------------------------------------------
  // Item handlers
  // ---------------------------------------------------------------------------

  const handleToggleItem = useCallback(
    async (item: TodoItemResponse) => {
      captureSnapshot();
      // In review mode, checking an item submits it for review rather than
      // completing it; unchecking a review/completed item reopens it. Outside
      // review mode, the checkbox is the classic pending ↔ completed toggle.
      const newStatus = reviewMode
        ? item.status === 'in_review' || item.status === 'completed'
          ? 'pending'
          : 'in_review'
        : item.status === 'completed'
          ? 'pending'
          : 'completed';
      await updateItem({ itemId: item.itemId, input: { status: newStatus } });
    },
    [captureSnapshot, updateItem, reviewMode],
  );

  // Verify a review item (in_review → completed). The acting user is recorded
  // server-side as the verifier (completed_by).
  const handleApproveItem = useCallback(
    async (item: TodoItemResponse) => {
      captureSnapshot();
      await updateItem({ itemId: item.itemId, input: { status: 'completed' } });
    },
    [captureSnapshot, updateItem],
  );

  // Send a review item back to active work (in_review → in_progress).
  const handleRejectItem = useCallback(
    async (item: TodoItemResponse) => {
      captureSnapshot();
      await updateItem({ itemId: item.itemId, input: { status: 'in_progress' } });
    },
    [captureSnapshot, updateItem],
  );

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      captureSnapshot();
      await deleteItem(itemId);
      toast({
        title: 'Item deleted',
        action: (
          <ToastAction altText="Undo" onClick={undo}>
            Undo
          </ToastAction>
        ),
      });
    },
    [captureSnapshot, deleteItem, toast, undo],
  );

  const handleEditItem = useCallback(
    (item: TodoItemResponse) => {
      dialogs.setEditingItem(item);
      dialogs.setEditItemOpen(true);
    },
    [dialogs],
  );

  const handleSaveItem = useCallback(
    async (
      itemId: string,
      input: { title?: string; description?: string | null; sectionId?: string | null },
    ) => {
      captureSnapshot();
      await updateItem({ itemId, input });
      dialogs.setEditItemOpen(false);
      dialogs.setEditingItem(null);
    },
    [captureSnapshot, updateItem, dialogs],
  );

  const handleUpdateItemTitle = useCallback(
    (itemId: string, title: string) => {
      captureSnapshot();
      updateItem({ itemId, input: { title } });
    },
    [captureSnapshot, updateItem],
  );

  const handleCreateItem = useCallback(
    async (input: { title: string; description?: string; sectionId?: string }) => {
      captureSnapshot();
      await createItem(input);
      dialogs.setCreateItemOpen(false);
    },
    [captureSnapshot, createItem, dialogs],
  );

  const handleCreateItemBelow = useCallback(
    async (sectionId: string | null, afterItemId: string, title: string) => {
      captureSnapshot();
      const newItem = await createItem({ title, sectionId: sectionId ?? undefined });
      // Re-derive the ordered id list from the live items snapshot — the
      // V-1 reorder verb takes a whole-list order rather than per-section.
      const sectionItems = items
        .filter((i) => i.sectionId === sectionId && i.status !== 'completed')
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const ids = sectionItems.map((i) => i.itemId);
      const afterIndex = ids.indexOf(afterItemId);
      ids.splice(afterIndex + 1, 0, newItem.itemId);
      // V-1: reorder operates per list, not per section. Build the full
      // ordered list by interleaving the existing items with the new one.
      const otherItems = items
        .filter((i) => i.sectionId !== sectionId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((i) => i.itemId);
      await reorderItems([...otherItems, ...ids]);
      queryClient.invalidateQueries({ queryKey: itemsKey });
    },
    [captureSnapshot, createItem, items, queryClient, itemsKey, reorderItems],
  );

  // ---------------------------------------------------------------------------
  // Section handlers
  // ---------------------------------------------------------------------------

  const handleEditSection = useCallback(
    (section: TodoSectionResponse) => {
      dialogs.setEditingSection(section);
      dialogs.setEditSectionOpen(true);
    },
    [dialogs],
  );

  const handleSaveSection = useCallback(
    async (
      sectionId: string,
      input: { name?: string; description?: string | null; parentSectionId?: string | null },
    ) => {
      captureSnapshot();
      await updateSection({ sectionId, input });
      dialogs.setEditSectionOpen(false);
      dialogs.setEditingSection(null);
    },
    [captureSnapshot, updateSection, dialogs],
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      captureSnapshot();
      deleteSection(sectionId);
    },
    [captureSnapshot, deleteSection],
  );

  const handleCreateSection = useCallback(
    async (input: {
      name: string;
      description?: string;
      parentSectionId?: string;
      sectionType: 'category';
    }) => {
      captureSnapshot();
      await createSection(input);
      dialogs.setCreateSectionOpen(false);
    },
    [captureSnapshot, createSection, dialogs],
  );

  const handleCreateTimeframe = useCallback(
    async (input: { name: string; description?: string; sectionType: 'timeframe' }) => {
      captureSnapshot();
      await createSection(input);
      dialogs.setCreateTimeframeOpen(false);
    },
    [captureSnapshot, createSection, dialogs],
  );

  const handleToggleHiddenFromPublic = useCallback(
    (sectionId: string, hidden: boolean) => {
      captureSnapshot();
      updateSection({ sectionId, input: { hiddenFromPublic: hidden } });
    },
    [captureSnapshot, updateSection],
  );

  // ---------------------------------------------------------------------------
  // List-level handlers (settings / delete)
  // ---------------------------------------------------------------------------

  const handleSettingsSave = useCallback(
    async (input: { isPublic?: boolean; publicSlug?: string | null; reviewMode?: boolean }) => {
      await api.updateTodoList(holderId, listId, input);
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: myKey });
    },
    [holderId, listId, queryClient, detailKey, myKey],
  );

  const handleDeleteList = useCallback(async () => {
    await api.deleteTodoList(holderId, listId);
    queryClient.removeQueries({ queryKey: detailKey });
    await queryClient.invalidateQueries({ queryKey: myKey });
  }, [holderId, listId, queryClient, detailKey, myKey]);

  // sections is unused by the per-handler logic but accepted in props so
  // a future reorder optimization can read pre-update order without
  // another query.
  void sections;

  return {
    optimisticSectionReorder,
    optimisticItemReorder,
    createTopLevelDragEnd,
    createUnsortedItemDragEnd,
    handleToggleItem,
    handleApproveItem,
    handleRejectItem,
    handleDeleteItem,
    handleEditItem,
    handleSaveItem,
    handleUpdateItemTitle,
    handleCreateItem,
    handleCreateItemBelow,
    handleEditSection,
    handleSaveSection,
    handleDeleteSection,
    handleCreateSection,
    handleCreateTimeframe,
    handleToggleHiddenFromPublic,
    handleSettingsSave,
    handleDeleteList,
  };
}
